import * as Yup from "yup";
import { constant } from "../../Global/Constant";
import { isDateOfBirthNotFuture, parseLocalDateInput } from "./userFormHelpers";

export const VALID_GENDER_VALUES = ["Male", "Female", "Non-binary", "Other", "Not stated"];
export const VALID_MARITAL_STATUS_VALUES = ["Single", "Married"];
export const VALID_BLOOD_GROUP_VALUES = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];

const ALPHA_SPACE_REGEX = constant.AlphaSpaceRegex;

function isEmpty(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

function isValidEnumValue(value, allowed) {
  if (isEmpty(value)) return true;
  return allowed.some((item) => item.toLowerCase() === String(value).toLowerCase());
}

export function createAdditionalProfileValidationSchema(t) {
  const maxChars = (max, fieldKey) =>
    t("app.users.validationMaxLength", { max, field: t(fieldKey) });
  const onlyAlphabets = t("app.users.validationOnlyAlphabets");

  return Yup.object().shape({
    employeeCode: Yup.string()
      .nullable()
      .max(20, maxChars(20, "app.users.employeeCode")),
    gender: Yup.string()
      .nullable()
      .max(10, maxChars(10, "app.users.gender"))
      .test("valid-gender", t("app.users.validationGenderInvalid"), (value) =>
        isValidEnumValue(value, VALID_GENDER_VALUES)
      ),
    dob: Yup.string()
      .nullable()
      .test("valid-date", t("app.users.validationDobInvalid"), (value) =>
        isEmpty(value) || parseLocalDateInput(value) !== null
      )
      .test("not-future", t("app.users.validationDobNotFuture"), isDateOfBirthNotFuture),
    maritalStatus: Yup.string()
      .nullable()
      .max(20, maxChars(20, "app.users.maritalStatus"))
      .test("valid-marital-status", t("app.users.validationMaritalStatusInvalid"), (value) =>
        isValidEnumValue(value, VALID_MARITAL_STATUS_VALUES)
      ),
    fatherOrHusbandName: Yup.string()
      .nullable()
      .max(70, maxChars(70, "app.users.fatherOrHusbandName"))
      .test("alpha-space", onlyAlphabets, (value) =>
        isEmpty(value) || ALPHA_SPACE_REGEX.test(String(value))
      ),
    cnic: Yup.string()
      .nullable()
      .max(50, maxChars(50, "app.users.nationalId")),
    phoneNumber: Yup.string()
      .nullable()
      .max(30, maxChars(30, "app.users.phoneNumber")),
    emergencyContactNumber: Yup.string()
      .nullable()
      .max(30, maxChars(30, "app.users.emergencyContactNumber")),
    emergencyContactName: Yup.string()
      .nullable()
      .max(70, maxChars(70, "app.users.emergencyContactName"))
      .test("alpha-space", onlyAlphabets, (value) =>
        isEmpty(value) || ALPHA_SPACE_REGEX.test(String(value))
      ),
    bloodGroup: Yup.string()
      .nullable()
      .max(3, maxChars(3, "app.users.bloodGroup"))
      .test("valid-blood-group", t("app.users.validationBloodGroupInvalid"), (value) =>
        isEmpty(value) || VALID_BLOOD_GROUP_VALUES.includes(String(value))
      ),
    passportNumber: Yup.string()
      .nullable()
      .max(50, maxChars(50, "app.users.passportNumber")),
    ntn: Yup.string()
      .nullable()
      .max(50, maxChars(50, "app.users.taxNumber")),
    disability: Yup.boolean().nullable(),
    disabilityDescription: Yup.string()
      .nullable()
      .max(250, maxChars(250, "app.users.disabilityDescription"))
      .when("disability", {
        is: true,
        then: (schema) =>
          schema.required(t("app.users.validationDisabilityDescriptionRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
    postalCode: Yup.string()
      .nullable()
      .max(20, maxChars(20, "app.users.postalCode")),
    city: Yup.string()
      .nullable()
      .max(100, maxChars(100, "app.users.city")),
    countryId: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined ? null : value
      )
      .test("valid-country", t("app.users.validationCountryInvalid"), (value) =>
        value === null || value === undefined || (Number.isFinite(value) && value > 0)
      ),
    permanentAddress: Yup.string()
      .nullable()
      .max(1000, maxChars(1000, "app.users.permanentAddress")),
    residentialAddress: Yup.string()
      .nullable()
      .max(1000, maxChars(1000, "app.users.residentialAddress")),
    dependents: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string()
            .trim()
            .required(t("app.users.validationDependentNameRequired"))
            .max(100, maxChars(100, "app.users.dependentName")),
          relationship: Yup.string().nullable().max(20, maxChars(20, "app.users.dependentRelationship")),
          dob: Yup.string()
            .nullable()
            .test("valid-date", t("app.users.validationDobInvalid"), (value) =>
              isEmpty(value) || parseLocalDateInput(value) !== null
            )
            .test("not-future", t("app.users.validationDobNotFuture"), isDateOfBirthNotFuture),
          gender: Yup.string()
            .nullable()
            .max(10, maxChars(10, "app.users.dependentGender"))
            .test("valid-gender", t("app.users.validationGenderInvalid"), (value) =>
              isValidEnumValue(value, VALID_GENDER_VALUES)
            ),
          contactNumber: Yup.string().nullable().max(30, maxChars(30, "app.users.dependentContactNumber")),
        })
      )
      .nullable(),
  });
}

/** Field metadata for the validation guide panel */
export function getAdditionalProfileFieldRules(t) {
  return [
    { id: "employeeCode", label: t("app.users.employeeCode"), requirement: t("app.users.validationRuleEmployeeCode") },
    { id: "gender", label: t("app.users.gender"), requirement: t("app.users.validationRuleGender") },
    { id: "dob", label: t("app.users.dateOfBirth"), requirement: t("app.users.validationRuleDob") },
    { id: "maritalStatus", label: t("app.users.maritalStatus"), requirement: t("app.users.validationRuleMaritalStatus") },
    { id: "fatherOrHusbandName", label: t("app.users.fatherOrHusbandName"), requirement: t("app.users.validationRuleAlphaName") },
    { id: "cnic", label: t("app.users.nationalId"), requirement: t("app.users.validationRuleCnic") },
    { id: "phoneNumber", label: t("app.users.phoneNumber"), requirement: t("app.users.validationRulePhone") },
    { id: "emergencyContactName", label: t("app.users.emergencyContactName"), requirement: t("app.users.validationRuleAlphaName") },
    { id: "emergencyContactNumber", label: t("app.users.emergencyContactNumber"), requirement: t("app.users.validationRulePhone") },
    { id: "bloodGroup", label: t("app.users.bloodGroup"), requirement: t("app.users.validationRuleBloodGroup") },
    { id: "passportNumber", label: t("app.users.passportNumber"), requirement: t("app.users.validationRulePassport") },
    { id: "ntn", label: t("app.users.taxNumber"), requirement: t("app.users.validationRuleNtn") },
    { id: "disability", label: t("app.users.disability"), requirement: t("app.users.validationRuleDisability") },
    { id: "disabilityDescription", label: t("app.users.disabilityDescription"), requirement: t("app.users.validationRuleDisabilityDescription") },
    { id: "postalCode", label: t("app.users.postalCode"), requirement: t("app.users.validationRulePostalCode") },
    { id: "city", label: t("app.users.city"), requirement: t("app.users.validationRuleCity") },
    { id: "countryId", label: t("app.users.country"), requirement: t("app.users.validationRuleCountry") },
    { id: "residentialAddress", label: t("app.users.residentialAddress"), requirement: t("app.users.validationRuleAddress") },
    { id: "permanentAddress", label: t("app.users.permanentAddress"), requirement: t("app.users.validationRuleAddress") },
  ];
}

/** Dynamic dependent name rules for the validation guide (one entry per added dependent). */
export function getDependentFieldRules(t, dependents = []) {
  if (!Array.isArray(dependents) || dependents.length === 0) return [];
  return dependents.map((_, index) => ({
    id: `dependentName_${index}`,
    touchPath: `additionalProfile.dependents.${index}.name`,
    yupPath: `dependents[${index}].name`,
    label: t("app.users.dependentNumber", { number: index + 1 }) + " — " + t("app.users.dependentName"),
    requirement: t("app.users.validationRuleDependentName"),
  }));
}

export function getAdditionalProfileFieldError(errors, fieldId) {
  if (!errors?.additionalProfile) return null;
  const profileErrors = errors.additionalProfile;
  if (typeof profileErrors === "string") return profileErrors;
  return profileErrors[fieldId] || null;
}

export function getAdditionalProfileFieldTouched(touched, fieldId) {
  if (!touched?.additionalProfile) return false;
  const profileTouched = touched.additionalProfile;
  if (typeof profileTouched === "boolean") return profileTouched;
  return Boolean(profileTouched[fieldId]);
}

export function validateAdditionalProfileSync(schema, profile) {
  try {
    schema.validateSync(profile || {}, { abortEarly: false });
    return {};
  } catch (err) {
    if (!err.inner) {
      const path = normalizeYupPath(err.path);
      return path ? { [path]: err.message } : { [err.path || "_"]: err.message };
    }
    return err.inner.reduce((acc, item) => {
      const path = normalizeYupPath(item.path);
      if (path) acc[path] = item.message;
      return acc;
    }, {});
  }
}

/** Convert Yup path `dependents[0].name` → `dependents.0.name` for nested lookups. */
export function normalizeYupPath(path) {
  if (!path) return path;
  return String(path).replace(/\[(\d+)\]/g, ".$1");
}

export function getAdditionalProfileValidationIssues(profile, formikErrors, schema) {
  const liveErrors = validateAdditionalProfileSync(schema, profile);
  const fieldIds = [
    "employeeCode", "gender", "dob", "maritalStatus", "fatherOrHusbandName", "cnic",
    "phoneNumber", "emergencyContactNumber", "emergencyContactName", "bloodGroup",
    "passportNumber", "ntn", "disability", "disabilityDescription",
    "postalCode", "city", "countryId", "residentialAddress", "permanentAddress",
  ];

  const staticIssues = fieldIds
    .map((fieldId) => {
      const message =
        getAdditionalProfileFieldError(formikErrors, fieldId) || liveErrors[fieldId] || null;
      return message ? { fieldId, message } : null;
    })
    .filter(Boolean);

  const dependents = Array.isArray(profile?.dependents) ? profile.dependents : [];
  const dependentIssues = dependents
    .map((_, index) => {
      const fieldId = `dependents.${index}.name`;
      const formikMsg = getAdditionalProfileFieldError(formikErrors, fieldId)
        || (Array.isArray(formikErrors?.additionalProfile?.dependents)
          ? formikErrors.additionalProfile.dependents[index]?.name
          : null);
      const message = formikMsg || liveErrors[fieldId] || null;
      return message ? { fieldId, message } : null;
    })
    .filter(Boolean);

  return [...staticIssues, ...dependentIssues];
}
